import React, { useMemo, useState } from 'react';

import { Box, Center, HStack, Stack, useDisclosure } from '@chakra-ui/react';
import { some } from 'lodash';
import { useSelector } from 'react-redux';

import {
  ScanVaultCentralAccount,
  ScanVaultCentralAccountStatus,
} from 'api/scan-vault/client';
import {
  getIcon,
  renderStatusTag,
  renderTableHeaderWithLoader,
  renderTimeStackedCell,
  StackedCell,
  WithResourceIcon,
} from 'components/DataDisplay';
import { CustomTooltip } from 'components/DataDisplay/Tooltip/CustomTooltip';
import { ActionButton, ButtonGroupItem } from 'components/DataEntry';
import {
  DetachIcon,
  IconTypes,
  PauseIcon,
  PlayIcon,
  RegionIcon,
} from 'components/Icons';
import { selectApp } from 'containers/App/selectors';
import { useCloudAccountId } from 'containers/Setup/CloudAccounts/utils';
import { selectOutpost } from 'containers/Setup/Outpost/selectors';

export const useOutpostTableColumns = () => {
  const { resourceDetails } = useSelector(selectOutpost);
  const { user } = useSelector(selectApp);
  const { cloudAccountIDMapping } = useCloudAccountId();
  const updateRegionsDisclosure = useDisclosure();
  const detachAccountDisclosure = useDisclosure();
  const [currentRow, setCurrentRow] = useState<ScanVaultCentralAccount>(
    {} as ScanVaultCentralAccount,
  );

  const outpostViewByOptions = useMemo(
    () => [
      { label: 'Ariksa', value: 'Ariksa' },
      { label: user.info.org_id, value: 'for_data' },
    ],
    [user.info.org_id],
  );

  const [viewBy, setViewBy] = useState<ButtonGroupItem>(
    outpostViewByOptions[0],
  );

  const status = {
    header: 'Status',
    accessor: 'status',
    align: 'left',
    render: ({ value }) => renderStatusTag(value),
  };

  const columns = [
    {
      header: (
        <Box pl={10}>
          {renderTableHeaderWithLoader(
            'Name',
            some(resourceDetails, { isLoading: true }),
          )}
        </Box>
      ),
      accessor: 'name',
      align: 'left',
      render: ({ row }) => (
        <WithResourceIcon
          resourceType={
            row?.type === 'Data'
              ? IconTypes.DataScan
              : IconTypes.OutpostForVulnerability
          }
        >
          <StackedCell
            upper={
              resourceDetails?.[row?.native_name]?.data?.[row?.instance_id]
                ?.name
            }
            lower={row.instance_id}
          />
        </WithResourceIcon>
      ),
    },
    {
      header: 'Deployed in',
      accessor: 'cloud',
      align: 'left',
      render: ({ row }) => (
        <StackedCell
          upper={
            <HStack align="baseline">
              <Box boxSize={5}>
                <Center>{getIcon(row?.cloud)}</Center>
              </Box>
              <Box>{row?.account_name ?? row?.cloud_account_id}</Box>
            </HStack>
          }
          lower={row?.region}
        />
      ),
    },
    {
      header: 'Type',
      accessor: 'type',
      align: 'left',
    },
    /*{
      header: 'Deployed In',
      align: 'left',
      render: ({ row }) => (
        <StackedCell
          upper={row?.region}
          lower={
            resourceDetails.data?.[row?.instance_id]?.vpc ||
            resourceDetails.data?.[row?.instance_id]?.vpc_id
          }
        />
      ),
    },*/
    {
      header: 'Created',
      accessor: 'first_seen',
      align: 'left',
      render: renderTimeStackedCell,
    },
    {
      header: 'Last Sync',
      accessor: 'last_seen',
      align: 'left',
      render: renderTimeStackedCell,
    },
    status,
    /*{
      header: 'Actions',
      accessor: 'actions',
      render: ({ row }) => {
        return (
          <Center>
            {row?.type === 'Vulnerability' && (
              <ActionButton
                icon={<RefreshIcon />}
                onClick={() => {
                  dispatch(
                    actions.rescanResources({
                      q: {
                        scannerStatus: {
                          account_id: row.account_id,
                          region: row?.region,
                          scanner_id: row?.scanner_id,
                          settings: { ...row?.settings, restart: true },
                          id: row?.id,
                          first_seen: row?.first_seen,
                          last_seen: row?.last_seen,
                        },
                      },
                    }),
                  );
                }}
                label={'Rescan resources'}
              />
            )}
          </Center>
        );
      },
    },*/
  ];

  const accountsColumns = [
    {
      header: <Box pl={10}>Account</Box>,
      accessor: 'name',
      align: 'left',
      render: ({ row }) => {
        const account = cloudAccountIDMapping?.[row?.account_id];
        return (
          <WithResourceIcon resourceType={row?.provider}>
            <StackedCell
              upper={account?.name ?? row?.account_id}
              lower={account?.cloud_account_id}
            />
          </WithResourceIcon>
        );
      },
    },
    {
      header: 'Regions Enabled',
      accessor: 'effective_enabled_regions',
      align: 'center',
      render: ({ value }) => {
        const regions = value ?? [];

        return regions.length ? (
          <CustomTooltip
            label={
              <Stack spacing={1}>
                {regions.map(region => (
                  <Box key={region}>{region}</Box>
                ))}
              </Stack>
            }
          >
            <Box>{regions.length}</Box>
          </CustomTooltip>
        ) : (
          <Box>0</Box>
        );
      },
    },
    {
      header: 'Enabled At',
      accessor: 'created_at',
      align: 'left',
      render: renderTimeStackedCell,
    },
    {
      header: 'Last Scan',
      accessor: 'last_scan_at',
      align: 'left',
      render: ({ value }) =>
        value ? (
          renderTimeStackedCell({ value })
        ) : (
          <Box color="gray.400">—</Box>
        ),
    },
    status,
    {
      header: 'Actions',
      render: ({ row }) => {
        return (
          <Center>
            <ActionButton
              label={
                (row.status === ScanVaultCentralAccountStatus.Inactive
                  ? 'Start'
                  : 'Stop') + ' scanning'
              }
              icon={
                row.status === ScanVaultCentralAccountStatus.Inactive ? (
                  <PlayIcon />
                ) : (
                  <PauseIcon />
                )
              }
              onClick={() => {
                detachAccountDisclosure.onOpen();
                setCurrentRow(row);
              }}
            />
            <ActionButton
              label="Update regions"
              icon={<RegionIcon />}
              onClick={() => {
                updateRegionsDisclosure.onOpen();
                setCurrentRow(row);
              }}
            />
          </Center>
        );
      },
    },
  ];

  return {
    accountsColumns,
    columns,
    viewBy,
    setViewBy,
    outpostViewByOptions,
    updateRegionsDisclosure,
    detachAccountDisclosure,
    currentRow,
  };
};
